import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { teacherScheduleApi } from '../../../services/teacherScheduleApi';

/**
 * Get left border color based on notification type and priority
 */
function getNotificationBorderColor(type) {
  switch (type) {
    case 'event_cancelled':
    case 'leave_rejected':
      return 'border-l-4 border-red-500';
    case 'results_published':
    case 'leave_approved':
      return 'border-l-4 border-green-500';
    case 'event_assignment':
      return 'border-l-4 border-blue-500';
    case 'day_before_reminder':
      return 'border-l-4 border-blue-400';
    case 'attendance_nudge':
    case 'event_updated':
      return 'border-l-4 border-amber-400';
    default:
      return 'border-l-4 border-blue-400';
  }
}

/**
 * Get navigation route for a notification based on its type and template_vars
 */
function getNotificationRoute(notification) {
  const vars = notification.template_vars || {};
  switch (notification.notification_type) {
    case 'event_assignment':
    case 'event_updated':
    case 'event_cancelled':
    case 'day_before_reminder':
      return vars.event_date
        ? `/teacher/schedule?date=${vars.event_date}`
        : '/teacher/schedule';
    case 'attendance_nudge':
      return vars.class_id && vars.division_id
        ? `/teacher/class/${vars.class_id}/division/${vars.division_id}?sheet=attendance`
        : '/teacher';
    case 'results_published':
      return '/teacher/reports';
    case 'leave_approved':
    case 'leave_rejected':
      return '/teacher/schedule';
    default:
      return null;
  }
}

/**
 * Get icon based on notification type (motif-consistent colors)
 */
function getNotificationIcon(type) {
  switch (type) {
    case 'event_assignment':
      return (
        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      );
    case 'event_cancelled':
      return (
        <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    case 'event_updated':
      return (
        <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      );
    case 'day_before_reminder':
      return (
        <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      );
    case 'results_published':
      return (
        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
      );
    case 'attendance_nudge':
      return (
        <div className="w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        </div>
      );
    case 'leave_approved':
      return (
        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    case 'leave_rejected':
      return (
        <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>
      );
    default:
      return (
        <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
        </div>
      );
  }
}

/**
 * NotificationBell - Shows notification count and dropdown
 * Renders inside MaterialAppBar (white bg context)
 *
 * @param {Object} props
 * @param {number} props.count - Unread count from parent (TeacherLayout polls)
 * @param {Function} props.onCountChange - Callback to refresh count after mark-read
 */
export default function NotificationBell({ count = 0, onCountChange }) {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notifications when dropdown opens
  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await teacherScheduleApi.listNotifications({ unreadOnly: true, limit: 10 });
      setNotifications(response.notifications || []);
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleToggle = useCallback(() => {
    const newOpen = !isOpen;
    setIsOpen(newOpen);
    if (newOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleNotificationClick = useCallback(async (notification) => {
    try {
      await teacherScheduleApi.markNotificationRead(notification.id);
      setNotifications((prev) => prev.filter((n) => n.id !== notification.id));
      if (onCountChange) onCountChange();
    } catch (e) {
      console.error('Failed to mark notification as read:', e);
    }

    // Navigate to relevant screen
    const route = getNotificationRoute(notification);
    if (route) {
      setIsOpen(false);
      navigate(route);
    }
  }, [navigate, onCountChange]);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await teacherScheduleApi.markAllNotificationsRead();
      setNotifications([]);
      if (onCountChange) onCountChange();
    } catch (e) {
      console.error('Failed to mark all as read:', e);
    }
  }, [onCountChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Check if notification is expired
  const isExpired = (notification) => {
    if (!notification.expires_at) return false;
    return new Date(notification.expires_at) < new Date();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button — styled for white-on-primary AppBar */}
      <button
        type="button"
        onClick={handleToggle}
        className="relative p-sm rounded-full hover:bg-white/10 active:bg-white/20 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50"
        aria-label={`Notifications${count > 0 ? ` (${count} unread)` : ''}`}
      >
        <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {/* Badge */}
        {count > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-1rem)] max-h-96 overflow-y-auto bg-white rounded-lg shadow-elevation-2 border border-gray-200 z-50 animate-spring-bouncy">
          {/* Header */}
          <div className="flex items-center justify-between p-lg border-b border-gray-100">
            <h3 className="text-mobile-h3 font-semibold text-gray-800">Notifications</h3>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                className="text-mobile-body text-blue-600 font-medium"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Content */}
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="p-lg text-center text-gray-500">
                <div className="animate-pulse">Loading...</div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-xl text-center">
                <div className="w-12 h-12 mx-auto mb-md rounded-full bg-gray-100 flex items-center justify-center">
                  <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                </div>
                <div className="text-caption text-gray-500">No new notifications</div>
              </div>
            ) : (
              notifications.map((notification) => {
                const expired = isExpired(notification);
                return (
                  <div
                    key={notification.id}
                    className={`flex items-start gap-md p-lg hover:bg-gray-50 transition-colors cursor-pointer ${
                      expired ? '' : getNotificationBorderColor(notification.notification_type)
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleNotificationClick(notification)}
                  >
                    {getNotificationIcon(notification.notification_type)}
                    <div className="flex-1 min-w-0">
                      <p className={`text-mobile-body font-medium line-clamp-2 ${
                        expired ? 'text-gray-400' : 'text-gray-800'
                      }`}>
                        {notification.rendered_message}
                        {expired && ' (Past)'}
                      </p>
                      <p className="text-caption text-gray-500 mt-xs">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* See all link */}
          {!loading && (
            <div className="border-t border-gray-100 p-md text-center">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/teacher/notifications');
                }}
                className="text-mobile-body text-blue-600 font-medium"
              >
                See all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
